import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WorkScheduleService } from '@services';
import { Events } from '@services/events/events.service';
import { WorkScheduleEntry, WorkScheduleType } from '@services/work-schedule/model';
import * as moment from 'moment';

interface DayCell {
  date: Date;
  dayOfMonth: number;
  dayOfWeek: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWorkTime: boolean;
  isDayOff: boolean;
  workPeriods: { startTime: string; endTime: string }[];
}

@Component({
  selector: 'app-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrls: ['./schedule-calendar.component.scss'],
})
export class ScheduleCalendarComponent implements OnInit, OnChanges, OnDestroy {
  // Schedule source inputs - provide one of these
  @Input() scheduleID: number;           // Single schedule ID (legacy mode)
  @Input() workDaysScheduleID: number;   // Work days schedule ID for combined view
  @Input() daysOffScheduleID: number;    // Days off schedule ID for combined view
  @Input() locationID: number;           // Location ID for location-specific view
  @Input() assetID: number;              // Asset ID for asset-specific entries
  @Input() folderID: number;             // Folder ID for folder-specific entries
  @Input() subjectType: string;          // Subject type (e.g., 'assetFolder', 'asset')
  @Input() subjectID: number;            // Subject ID for subject-specific entries

  // Display options
  @Input() month: number;                // Display month (1-12), defaults to current
  @Input() year: number;                 // Display year, defaults to current
  @Input() viewMode: 'month' | 'week' = 'month';  // Calendar view mode
  @Input() showLegend: boolean = true;   // Show color legend
  @Input() readonly: boolean = true;     // Read-only or interactive
  @Input() highlightToday: boolean = true; // Highlight current day
  @Input() compact: boolean = false;     // Compact view (smaller cells)

  // Legend label overrides (for different contexts like assets vs workers)
  // Pass translation keys - component will translate them
  @Input() workDaysLabelKey: string = 'WORK_SCHEDULE.Work_Days';
  @Input() daysOffLabelKey: string = 'WORK_SCHEDULE.Days_Off';

  // Output events
  @Output() daySelected = new EventEmitter<{ date: Date; isWorkTime: boolean }>();
  @Output() monthChanged = new EventEmitter<{ month: number; year: number }>();

  // Calendar state
  public weeks: DayCell[][] = [];
  public weekDayHeaders: string[] = [];
  public displayMonth: string = '';
  public displayYear: number;
  public loading: boolean = false;

  // Schedule data
  private workDaysSchedule: any = null;
  private daysOffSchedule: any = null;
  private workDaysEntries: WorkScheduleEntry[] = [];
  private daysOffEntries: WorkScheduleEntry[] = [];

  // Event handler for schedule updates
  private scheduleUpdateHandler = () => {
    this.loadScheduleData();
  };

  constructor(
    private translate: TranslateService,
    private workScheduleService: WorkScheduleService,
    private events: Events
  ) {}

  ngOnInit() {
    this.initWeekDayHeaders();
    this.initDate();
    this.loadScheduleData();

    // Subscribe to schedule updates to auto-refresh
    this.events.subscribe('ccs:workSchedulesUpdate', this.scheduleUpdateHandler);
  }

  ngOnDestroy() {
    this.events.unsubscribe('ccs:workSchedulesUpdate', this.scheduleUpdateHandler);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Ensure month/year are initialized before loading data
    // (ngOnChanges can fire before ngOnInit)
    if (!this.month || !this.year) {
      this.initDate();
    }

    if (changes['scheduleID'] || changes['locationID'] || changes['workDaysScheduleID'] ||
        changes['daysOffScheduleID'] || changes['subjectType'] || changes['subjectID'] ||
        changes['folderID'] || changes['assetID']) {
      this.loadScheduleData();
    } else if (changes['month'] || changes['year']) {
      this.buildCalendar();
    }
  }

  private initWeekDayHeaders() {
    // Short day names for header
    this.weekDayHeaders = [
      this.translate.instant('WORK_SCHEDULE.Sunday').substring(0, 3),
      this.translate.instant('WORK_SCHEDULE.Monday').substring(0, 3),
      this.translate.instant('WORK_SCHEDULE.Tuesday').substring(0, 3),
      this.translate.instant('WORK_SCHEDULE.Wednesday').substring(0, 3),
      this.translate.instant('WORK_SCHEDULE.Thursday').substring(0, 3),
      this.translate.instant('WORK_SCHEDULE.Friday').substring(0, 3),
      this.translate.instant('WORK_SCHEDULE.Saturday').substring(0, 3)
    ];
  }

  private initDate() {
    const now = new Date();
    if (!this.month) {
      this.month = now.getMonth() + 1; // getMonth() is 0-indexed
    }
    if (!this.year) {
      this.year = now.getFullYear();
    }
    this.updateDisplayStrings();
  }

  private updateDisplayStrings() {
    const monthDate = new Date(this.year, this.month - 1, 1);
    this.displayMonth = moment(monthDate).format('MMMM');
    this.displayYear = this.year;
  }

  // API-based calendar preview data (when using combined schedules)
  private calendarPreviewData: {
    workingDays: string[];
    daysOff: string[];
    dayDetails: { [date: string]: any };
  } | null = null;

  async loadScheduleData() {
    this.loading = true;
    this.calendarPreviewData = null;

    try {
      // Combined schedule mode: use backend API to compute effective schedule
      if (this.workDaysScheduleID !== undefined || this.daysOffScheduleID !== undefined) {
        await this.loadCombinedSchedulePreview();
      }
      // Legacy single schedule mode
      else if (this.scheduleID) {
        await this.workScheduleService.refreshWorkSchedules();
        const schedule = this.workScheduleService.getWorkScheduleByID(this.scheduleID);

        if (schedule) {
          // Backend already provides merged entries in schedule.entries (from effectiveEntries column)
          // which includes inherited entries from parent schedules with exclusions applied.
          // No need to manually merge parent entries here.
          const entries = [...(schedule.entries || [])];

          if (schedule.type === WorkScheduleType.Work_Days) {
            this.workDaysSchedule = schedule;
            this.workDaysEntries = entries;
            this.daysOffEntries = [];
          } else {
            this.daysOffSchedule = schedule;
            this.daysOffEntries = entries;
            this.workDaysEntries = [];
          }
        }
      }
      // Future: Handle locationID, assetID, folderID for effective schedule

      this.buildCalendar();
    } catch (error) {
      console.error('Error loading schedule data:', this.getErrorMessage(error));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Load calendar preview from backend API for combined work days + days off view
   * Falls back to loading individual schedules if API fails
   */
  private async loadCombinedSchedulePreview() {
    // Calculate date range for current month view (include adjacent weeks)
    const firstDayOfMonth = new Date(this.year, this.month - 1, 1);
    const lastDayOfMonth = new Date(this.year, this.month, 0);

    // Start from the first Sunday on or before the first day of the month
    const calendarStart = new Date(firstDayOfMonth);
    calendarStart.setDate(calendarStart.getDate() - calendarStart.getDay());

    // End on the last Saturday on or after the last day of the month
    const calendarEnd = new Date(lastDayOfMonth);
    const daysToAdd = 6 - calendarEnd.getDay();
    calendarEnd.setDate(calendarEnd.getDate() + daysToAdd);

    const startDate = moment(calendarStart).format('YYYY-MM-DD');
    const endDate = moment(calendarEnd).format('YYYY-MM-DD');

    try {
      // Determine subject type and ID for subject-specific entries
      let effectiveSubjectType = this.subjectType;
      let effectiveSubjectID = this.subjectID;

      // Support folderID and assetID as shortcuts
      if (!effectiveSubjectType && this.folderID && this.folderID > 0) {
        effectiveSubjectType = 'folder';
        effectiveSubjectID = this.folderID;
      } else if (!effectiveSubjectType && this.assetID && this.assetID > 0) {
        effectiveSubjectType = 'asset';
        effectiveSubjectID = this.assetID;
      }

      // Use location-aware API when locationID is provided (to resolve company defaults)
      let preview;
      if (this.locationID && this.locationID > 0) {
        preview = await this.workScheduleService.getCalendarPreviewForLocation(
          this.workDaysScheduleID || 0,
          this.daysOffScheduleID || 0,
          startDate,
          endDate,
          this.locationID,
          effectiveSubjectType,
          effectiveSubjectID
        );
      } else {
        preview = await this.workScheduleService.getCalendarPreview(
          this.workDaysScheduleID || 0,
          this.daysOffScheduleID || 0,
          startDate,
          endDate,
          effectiveSubjectType,
          effectiveSubjectID
        );
      }
      this.calendarPreviewData = {
        workingDays: preview.workingDays,
        daysOff: preview.daysOff,
        dayDetails: preview.dayDetails || {}
      };
    } catch (error) {
      console.error('Error loading calendar preview from API, falling back to local calculation:', this.getErrorMessage(error));
      this.calendarPreviewData = null;

      // Fallback: load individual schedules for local calculation
      await this.loadIndividualSchedules();
    }
  }

  private getErrorMessage(error: unknown): string {
    let message = '';

    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      try {
        message = JSON.stringify(error) ?? '[non-serializable error]';
      } catch {
        message = '[non-serializable error]';
      }
    }

    return message;
  }

  /**
   * Fallback: Load individual schedules for local calculation when API fails
   */
  private async loadIndividualSchedules() {
    await this.workScheduleService.refreshWorkSchedules();

    // Reset entries
    this.workDaysEntries = [];
    this.daysOffEntries = [];
    this.workDaysSchedule = null;
    this.daysOffSchedule = null;

    // Load work days schedule
    if (this.workDaysScheduleID && this.workDaysScheduleID > 0) {
      const schedule = this.workScheduleService.getWorkScheduleByID(this.workDaysScheduleID);
      if (schedule) {
        this.workDaysSchedule = schedule;
        this.workDaysEntries = schedule.entries || [];
      }
    }

    // Load days off schedule
    if (this.daysOffScheduleID && this.daysOffScheduleID > 0) {
      const schedule = this.workScheduleService.getWorkScheduleByID(this.daysOffScheduleID);
      if (schedule) {
        this.daysOffSchedule = schedule;
        this.daysOffEntries = schedule.entries || [];
      }
    }
  }

  private buildCalendar() {
    this.updateDisplayStrings();

    const firstDayOfMonth = new Date(this.year, this.month - 1, 1);
    const lastDayOfMonth = new Date(this.year, this.month, 0);

    // Start from the first Sunday on or before the first day of the month
    const calendarStart = new Date(firstDayOfMonth);
    calendarStart.setDate(calendarStart.getDate() - calendarStart.getDay());

    // End on the last Saturday on or after the last day of the month
    const calendarEnd = new Date(lastDayOfMonth);
    const daysToAdd = 6 - calendarEnd.getDay();
    calendarEnd.setDate(calendarEnd.getDate() + daysToAdd);

    this.weeks = [];
    let currentWeek: DayCell[] = [];
    const currentDate = new Date(calendarStart);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    while (currentDate <= calendarEnd) {
      const dayCell = this.createDayCell(currentDate, today);
      currentWeek.push(dayCell);

      if (currentWeek.length === 7) {
        this.weeks.push(currentWeek);
        currentWeek = [];
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  private createDayCell(date: Date, today: Date): DayCell {
    const dayOfWeek = date.getDay();
    const isCurrentMonth = date.getMonth() === this.month - 1;
    const isToday = date.getTime() === today.getTime();

    // Determine if this day is a work day and if there are days off
    const workStatus = this.calculateWorkStatus(date);

    return {
      date: new Date(date),
      dayOfMonth: date.getDate(),
      dayOfWeek: dayOfWeek,
      isCurrentMonth: isCurrentMonth,
      isToday: isToday,
      isWorkTime: workStatus.isWorkTime,
      isDayOff: workStatus.isDayOff,
      workPeriods: workStatus.workPeriods
    };
  }

  private calculateWorkStatus(date: Date): { isWorkTime: boolean; isDayOff: boolean; workPeriods: { startTime: string; endTime: string }[] } {
    const dayOfWeek = date.getDay();
    const dateStr = moment(date).format('YYYY-MM-DD');
    const workPeriods: { startTime: string; endTime: string }[] = [];

    // If we have API-based calendar preview data, use it
    if (this.calendarPreviewData) {
      const isWorkTime = this.calendarPreviewData.workingDays.includes(dateStr);
      const isDayOff = this.calendarPreviewData.daysOff.includes(dateStr);

      // Extract work periods from dayDetails
      const dayDetail = this.calendarPreviewData.dayDetails[dateStr];
      if (dayDetail) {
        // Prefer pre-calculated timeRanges (which account for exclusions)
        if (dayDetail.timeRanges && dayDetail.timeRanges.length > 0) {
          for (const range of dayDetail.timeRanges) {
            workPeriods.push({
              startTime: range.start,
              endTime: range.end
            });
          }
        } else if (dayDetail.entries) {
          // Fallback to extracting from entries (for days without timeRanges)
          for (const matchedEntry of dayDetail.entries) {
            if (matchedEntry.entry) {
              // For work days, only show work days schedule time ranges
              // For days off, only show days off schedule time ranges
              const entry = matchedEntry.entry;
              if (entry.startTime && entry.endTime) {
                if ((isWorkTime && matchedEntry.type === WorkScheduleType.Work_Days) ||
                    (isDayOff && matchedEntry.type === WorkScheduleType.Days_Off)) {
                  workPeriods.push({
                    startTime: entry.startTime,
                    endTime: entry.endTime
                  });
                }
              }
            }
          }
        }
      }

      // Sort work periods by start time
      workPeriods.sort((a, b) => a.startTime.localeCompare(b.startTime));

      return {
        isWorkTime,
        isDayOff,
        workPeriods
      };
    }

    // Legacy mode: calculate from local entries
    // Check work days entries
    let isWorkDay = false;
    const workDayPeriods: { startTime: string; endTime: string }[] = [];

    for (const entry of this.workDaysEntries) {
      if (this.entryMatchesDate(entry, date, dayOfWeek, dateStr)) {
        isWorkDay = true;
        if (entry.startTime && entry.endTime) {
          workDayPeriods.push({
            startTime: entry.startTime,
            endTime: entry.endTime
          });
        }
      }
    }

    // Check days off entries
    let isDayOff = false;
    const dayOffPeriods: { startTime: string; endTime: string }[] = [];

    for (const entry of this.daysOffEntries) {
      if (this.entryMatchesDate(entry, date, dayOfWeek, dateStr)) {
        isDayOff = true;
        if (entry.startTime && entry.endTime) {
          dayOffPeriods.push({
            startTime: entry.startTime,
            endTime: entry.endTime
          });
        }
      }
    }

    // Determine work time status:
    // - If work days schedule exists: day must be a work day AND not a day off
    // - If only days off schedule: all days are work days except days off
    // - If neither: all days are unscheduled (show as non-work)
    let isWorkTime = false;
    if (this.workDaysSchedule || this.workDaysEntries.length > 0) {
      // Have work days defined: must match a work day entry and not be a day off
      isWorkTime = isWorkDay && !isDayOff;
    } else if (this.daysOffSchedule || this.daysOffEntries.length > 0) {
      // Only days off defined: all days are work days except explicit days off
      isWorkTime = !isDayOff;
    }
    // else: no schedules loaded, isWorkTime stays false

    // Add appropriate time periods based on final status
    if (isDayOff) {
      workPeriods.push(...dayOffPeriods);
    } else if (isWorkTime) {
      workPeriods.push(...workDayPeriods);
    }

    // Sort work periods by start time
    workPeriods.sort((a, b) => a.startTime.localeCompare(b.startTime));

    return {
      isWorkTime,
      isDayOff,
      workPeriods
    };
  }

  /**
   * Check if a date falls within an entry's effective date bounds
   * Mirrors backend WorkTime.pm _matchesDate logic for recurring entries
   */
  private isDateWithinEntryBounds(entry: WorkScheduleEntry, dateStr: string): boolean {
    // Check effectiveDate
    let effectiveDate = entry.effectiveDate;
    if (!effectiveDate && entry.createdAt) {
      // Fall back to createdAt if effectiveDate not set (convert Unix timestamp to YYYY-MM-DD)
      const dt = new Date(entry.createdAt * 1000);
      effectiveDate = moment(dt).format('YYYY-MM-DD');
    }

    if (effectiveDate && dateStr < effectiveDate) {
      return false;  // Date is before entry becomes effective
    }

    // Check expiresDate
    if (entry.expiresDate && dateStr > entry.expiresDate) {
      return false;  // Date is after entry expires
    }

    return true;
  }

  private entryMatchesDate(entry: WorkScheduleEntry, date: Date, dayOfWeek: number, dateStr: string): boolean {
    // Check day of week entries (including multi-day)
    // Use Number() conversion to handle string vs number type mismatches
    if (entry.dayOfWeek !== undefined && entry.dayOfWeek !== null) {
      if (Number(entry.dayOfWeek) === dayOfWeek) {
        // Check effectiveDate/expiresDate bounds for recurring entries
        return this.isDateWithinEntryBounds(entry, dateStr);
      }
      return false;
    }

    if (entry.daysOfWeek && Array.isArray(entry.daysOfWeek)) {
      if (entry.daysOfWeek.some(d => Number(d) === dayOfWeek)) {
        // Check effectiveDate/expiresDate bounds for recurring entries
        return this.isDateWithinEntryBounds(entry, dateStr);
      }
      return false;
    }

    // Check date range entries (startDate/endDate)
    if (entry.startDate) {
      const entryStartDateStr = entry.startDate.substring(0, 10); // Handle datetime format
      const entryEndDateStr = (entry.endDate || entry.startDate).substring(0, 10);

      if (entry.repeatAnnually) {
        // For repeat annually, check month/day match regardless of year
        const startDate = new Date(entry.startDate);
        const endDate = new Date(entry.endDate || entry.startDate);

        // For same month-day range in current year
        const checkStartMonth = startDate.getMonth();
        const checkStartDay = startDate.getDate();
        const checkEndMonth = endDate.getMonth();
        const checkEndDay = endDate.getDate();

        const currentMonth = date.getMonth();
        const currentDay = date.getDate();

        // Simple check: is current date's month/day within the range?
        if (checkStartMonth === checkEndMonth) {
          // Same month - just check day range
          if (currentMonth === checkStartMonth &&
              currentDay >= checkStartDay && currentDay <= checkEndDay) {
            return true;
          }
        } else {
          // Spans months - check if current date is in range
          const yearForComparison = this.year;
          const adjustedStart = new Date(yearForComparison, checkStartMonth, checkStartDay);
          const adjustedEnd = new Date(yearForComparison, checkEndMonth, checkEndDay);
          if (date >= adjustedStart && date <= adjustedEnd) {
            return true;
          }
        }
      } else {
        // Not repeating - exact date range comparison
        if (dateStr >= entryStartDateStr && dateStr <= entryEndDateStr) {
          return true;
        }
      }
    }

    // Check relative date entries (US holidays)
    if (entry.relativeDayName) {
      const holidayDate = this.resolveRelativeDate(this.year, entry.relativeDayName, entry.relativeDayOffset || 0);
      if (holidayDate) {
        return holidayDate.getMonth() === date.getMonth() &&
               holidayDate.getDate() === date.getDate();
      }
    }

    return false;
  }

  /**
   * Resolve US holidays to actual dates
   * Note: This mirrors the backend WorkTime.pm logic
   */
  private resolveRelativeDate(year: number, holidayName: string, offset: number): Date | null {
    let date: Date | null = null;

    // Normalize holiday name: lowercase and replace spaces with underscores
    const normalizedName = holidayName.toLowerCase().replace(/\s+/g, '_');

    switch (normalizedName) {
      case 'new_years_day':
      case 'new_year':
        date = new Date(year, 0, 1);
        break;

      case 'mlk_day':
        // Third Monday of January
        date = this.getNthWeekdayOfMonth(year, 0, 1, 3);
        break;

      case 'presidents_day':
      case "president's_day":
        // Third Monday of February
        date = this.getNthWeekdayOfMonth(year, 1, 1, 3);
        break;

      case 'memorial_day':
        // Last Monday of May
        date = this.getLastWeekdayOfMonth(year, 4, 1);
        break;

      case 'juneteenth':
        date = new Date(year, 5, 19);
        break;

      case 'independence_day':
      case 'july_4th':
      case '4th_of_july':
        date = new Date(year, 6, 4);
        break;

      case 'labor_day':
        // First Monday of September
        date = this.getNthWeekdayOfMonth(year, 8, 1, 1);
        break;

      case 'columbus_day':
        // Second Monday of October
        date = this.getNthWeekdayOfMonth(year, 9, 1, 2);
        break;

      case 'veterans_day':
      case "veteran's_day":
        date = new Date(year, 10, 11);
        break;

      case 'thanksgiving':
      case 'thanksgiving_day':
        // Fourth Thursday of November
        date = this.getNthWeekdayOfMonth(year, 10, 4, 4);
        break;

      case 'christmas_day':
      case 'christmas':
        date = new Date(year, 11, 25);
        break;

      case 'good_friday':
        date = this.calculateGoodFriday(year);
        break;

      case 'easter':
        date = this.calculateEaster(year);
        break;

      default:
        return null;
    }

    if (date && offset !== 0) {
      date.setDate(date.getDate() + offset);
    }

    return date;
  }

  private getNthWeekdayOfMonth(year: number, month: number, dayOfWeek: number, n: number): Date {
    const date = new Date(year, month, 1);
    let count = 0;

    while (date.getMonth() === month) {
      if (date.getDay() === dayOfWeek) {
        count++;
        if (count === n) {
          return date;
        }
      }
      date.setDate(date.getDate() + 1);
    }

    return new Date(year, month, 1); // Fallback
  }

  private getLastWeekdayOfMonth(year: number, month: number, dayOfWeek: number): Date {
    const lastDay = new Date(year, month + 1, 0);
    const date = new Date(lastDay);

    while (date.getDay() !== dayOfWeek) {
      date.setDate(date.getDate() - 1);
    }

    return date;
  }

  private calculateEaster(year: number): Date {
    // Anonymous Gregorian algorithm
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(year, month, day);
  }

  private calculateGoodFriday(year: number): Date {
    const easter = this.calculateEaster(year);
    const goodFriday = new Date(easter);
    goodFriday.setDate(goodFriday.getDate() - 2);
    return goodFriday;
  }

  // Navigation methods
  previousMonth() {
    if (this.month === 1) {
      this.month = 12;
      this.year--;
    } else {
      this.month--;
    }
    this.onMonthChange();
  }

  nextMonth() {
    if (this.month === 12) {
      this.month = 1;
      this.year++;
    } else {
      this.month++;
    }
    this.onMonthChange();
  }

  goToToday() {
    const now = new Date();
    this.month = now.getMonth() + 1;
    this.year = now.getFullYear();
    this.onMonthChange();
  }

  private async onMonthChange() {
    // If using API-based preview, reload data for new month
    if (this.workDaysScheduleID !== undefined || this.daysOffScheduleID !== undefined) {
      await this.loadCombinedSchedulePreview();
    }
    this.buildCalendar();
    this.monthChanged.emit({ month: this.month, year: this.year });
  }

  // Interaction methods
  onDayClick(day: DayCell) {
    if (!this.readonly && day.isCurrentMonth) {
      this.daySelected.emit({
        date: day.date,
        isWorkTime: day.isWorkTime
      });
    }
  }

  // Style helpers
  getDayClasses(day: DayCell): string {
    const classes: string[] = ['calendar-day'];

    if (!day.isCurrentMonth) {
      classes.push('other-month');
    }

    if (day.isToday && this.highlightToday) {
      classes.push('today');
    }

    if (day.isWorkTime) {
      classes.push('work-time');
    } else {
      classes.push('non-work-time');
    }

    if (day.isDayOff) {
      classes.push('day-off');
    }

    if (!this.readonly) {
      classes.push('clickable');
    }

    return classes.join(' ');
  }
}
